import {
  map,
  isEmpty,
  isPlainObject,
  camelCase,
  isString,
  isArray,
} from 'lodash';
import {
  removeAuthCookie,
  DEFAULT_IB_AUTH_COOKIE_NAME,
} from 'web-lib-application-essentials/lib/cookie';
import { getAuthDomain } from 'web-lib-application-essentials/lib/urlUtil';
import envConfig from '../../static/config';
import SiteHeaderActionCreator from '../actions/SiteHeaderActionCreator';
import UserManagementStore from './UserManagementStore';
import AppDispatcher from '../dispatcher/AppDispatcher';
import InternetBankingActions from '../actions/InternetBankingActions';
import { EventEmitter } from 'events';
import { CHANGE_EVENT } from '../constants/AppConstants';
import SessionConstants from '../constants/SessionConstants';
import { CHALLENGE_TYPE } from '../constants/ChallengeConstants';
import SessionApiUtils from '../utils/SessionApiUtils';
import EncryptionUtils from '../utils/EncryptionUtils';
import GenericMapperUtils from '../utils/GenericMapperUtils';
import URLUtils from '../utils/UrlUtils';
import SessionActionCreator from '../actions/SessionActionCreator';

let _challenge = {};
let _challengeMissingDetailsError = false;
const _authentication = {};
let _targetScope;
let _passwordForAutoLogin; // Use only when auto-logging in the user after registration.
let _usernameForAutoLogin;
let _genericPublicKey; // Populated by the getPublicKey call (which does NOT use a user id)
let _genericPublicKeyDatetime;
let _allowRetry = true; // Whether to allow the user to retry authentication
let _isRequestingOtp = false;
let _isMakingOtpRequest = false;
let _otpRequestError = false;
let _isNavigatingToWebAppDuringValidSession = false;
let _authenticated = false;
let _callingGetCurrentUser = false;
let _retrievedUser = false;
let _userDetails = {};
let _sessionTimeout = false;
let _malwarePacket = {};
let _showPwResetReminder = false;

let _token = {};
let _tokenDelete = true;
let _isTokenError = false;

let _accountLockedErrorID = '';

let _isStepUpMode = false;
let _isStepUpCompleted = false;

let _isStepUpChallengeLoading = false;
let _isTokenLoading = false;

let _preferredOTPNumber = null;

// Timer ID.
let _lastActivityTime;

let urlAccessToken = '';
let sessionBankID;
// todo wtf is going on here!
let sessionBankIDError; // eslint-disable-line

const navigateToLoginPage = () => {
  window.location.href = URLUtils.formatLoginAndRegUrl(
    envConfig.loginAndRegistrationUrl,
    localStorage.getItem('uniqueId'),
    UserManagementStore.isSMEUser()
  );
};

const SessionStore = Object.assign({}, EventEmitter.prototype, {
  emitChange() {
    this.emit(CHANGE_EVENT);
  },

  /**
   * @param {function} callback
   */
  addChangeListener(callback) {
    this.on(CHANGE_EVENT, callback);
  },

  /**
   * Create a new challenge request. User can then respond with the appropriate info.
   *
   * @param  {String|UUID|Object} userIdentity  userIdentity to send the the API.
   * @param  {Number} scope      Level to step up to.
   * @param {Function} callback    Optional. Callback to run on completion.
   */
  createAuthChallenge(userIdentity, scope, callback) {
    _challenge = {};
    _targetScope = scope;

    SessionApiUtils.createAuthChallenge(userIdentity, _targetScope, callback);
  },

  /**
   * Respond to a challenge (i.e. step up the user) with the data
   * we currently have.
   *
   * @param  {String} username    userIdentity to be sent to the API.
   * @param  {Function} callback    An optional callback for on success of token create
   */
  createAuthToken(userIdentity, callback) {
    const data = {
      userIdentity,
      challengeResponse: _challenge,
      authData: _authentication,
    };

    SessionApiUtils.createAuthToken(data, _targetScope, callback);
  },

  /**
   * Allow views to unbind listeners before dismounting.
   *
   * @param  {Function} callback    Function to unbind.
   */
  removeChangeListener(callback) {
    this.removeListener(CHANGE_EVENT, callback);
  },

  /**
   * Automatically log the user in, with the given username
   * and password.
   *
   * @param  {String} username  As entered by the user.
   * @param  {String} password  As entered by the user.
   */
  startUserAutoLogin(username, password) {
    this.resetAccessToken(true);
    this.createAuthChallenge(username, envConfig.targetScope.login);

    _usernameForAutoLogin = username;
    _passwordForAutoLogin = password;
  },

  /**
   * Stage 2 of auto-login. Challenge should now have been created.
   * Respond to it, using the saved password.
   */
  completeUserAutoLogin() {
    const password = `${_passwordForAutoLogin}`;
    const username = `${_usernameForAutoLogin}`;

    _passwordForAutoLogin = undefined;
    _usernameForAutoLogin = undefined;

    const sessionId = _challenge['auth_session_id'];

    // Get the 3 random characters.
    _authentication['partial-password'] = map(
      _challenge['auth_schemes'][0].challenges['partial_password'].positions,
      (pos) => {
        const char = password.charAt(pos - 1);
        return EncryptionUtils.encrypt(
          _challenge['public_key'],
          `${sessionId}:${char}`
        );
      }
    );

    this.createAuthToken(username);
  },

  /**
   * Save a new access token.
   *
   * @param  {Object} data    API response.
   */
  saveNewAccessToken(data) {
    _token = data;
    _token.error = false;
    _lastActivityTime = new Date().getTime();

    /**
     * Sets new token into `urlAccessToken` field.
     * Originally comes from L&R query param and
     * has the highest priority within `ApiUtils.makeAjaxCall`.
     *
     * In case of additional trust level step-up,
     * the initial L&R token will be overriden across
     * all of the `SessionStore` and further API calls.
     */
    SessionStore.setURLTokenValue(data.access_token);

    /**
     * Modified to handle small device user inactivity (BOW-3165)
     */
    if (process.env.NODE_ENV === 'production') {
      document.onkeydown = this.restartInactivityTimer.bind(this);
      document.onchange = this.restartInactivityTimer.bind(this);
      document.onmousemove = this.restartInactivityTimer.bind(this);
      document.onmousedown = this.restartInactivityTimer.bind(this); // touchscreen presses
      document.ontouchstart = this.restartInactivityTimer.bind(this);
      document.ontouchmove = this.restartInactivityTimer.bind(this);
      document.ontouchend = this.restartInactivityTimer.bind(this);
      document.ontouchcancel = this.restartInactivityTimer.bind(this);
      document.onclick = this.restartInactivityTimer.bind(this); // touchpad clicks
      document.onscroll = this.restartInactivityTimer.bind(this); // scrolling with arrow keys
      document.onkeypress = this.restartInactivityTimer.bind(this);
    }
  },

  /**
   * Expire tokens after a set period of inactivity.
   * Clears any current timeout.
   *
   */
  restartInactivityTimer() {
    const activityTime = new Date().getTime();
    const diff = activityTime - _lastActivityTime;
    if (diff > envConfig.tokenInactivityTimeout) {
      this.resetAccessTokenOnInactivity();
    } else {
      _lastActivityTime = activityTime;
    }
  },

  /**
   * Delete Token on session inactivity
   */
  resetAccessTokenOnInactivity() {
    removeAuthCookie(DEFAULT_IB_AUTH_COOKIE_NAME, getAuthDomain());
    if (!isEmpty(_token)) {
      if (_token['access_token']) {
        SessionApiUtils.deleteAccessTokenOnInactivity(_token['access_token']);
        SiteHeaderActionCreator.setManageCredFlag(false);
      }
      _token = {};
      SessionStore.emitChange();
    }
  },

  /**
   * Clear any existing access token we currently hold.
   *
   * @param {Boolean} deleteLocalTokenOnly    Optional. Is the API call un-necessary here? Default false.
   */
  resetAccessToken(deleteToken) {
    removeAuthCookie(DEFAULT_IB_AUTH_COOKIE_NAME, getAuthDomain());
    if (!isEmpty(_token)) {
      if (deleteToken && _token['access_token']) {
        SessionApiUtils.deleteAccessToken(_token['access_token']);
      }
    }
  },

  getCurrentUser(userDetails, retrievedUser, callingGetCurrentUser) {
    // Stop mutiple calls if we already have this.
    if (retrievedUser || callingGetCurrentUser) {
      return null;
    }
    _callingGetCurrentUser = callingGetCurrentUser;

    SessionApiUtils.getCurrentUser((data) => {
      if (!data) {
        return false;
      }

      _userDetails = data;
      _retrievedUser = true;

      InternetBankingActions.updateUserName(this.getUserName(_userDetails));
      SessionStore.emitChange();
    });
  },

  /**
   * Set allow retry flag
   *
   * @param  {String} allowRetry    Allow Retry
   */
  setAllowRetry(allowRetry) {
    _allowRetry = allowRetry;
  },

  getChallenge() {
    return _challenge || {};
  },

  getToken() {
    return _token || {};
  },

  setTokenDelete(val) {
    _tokenDelete = val;
  },

  isTokenDelete() {
    return _tokenDelete;
  },

  getAuthentication() {
    return _authentication || '';
  },

  getAllowRetry() {
    return _allowRetry;
  },

  getAccountLockedQuoteID() {
    return _accountLockedErrorID;
  },

  getAll() {
    return {
      authenticated: _authenticated,
      accessToken: _token['access_token'],
      allowRetry: this.getAllowRetry(),
      authentication: this.getAuthentication(),
      challenge: this.getChallenge(),
      challengeMissingDetailsError: _challengeMissingDetailsError,
      genericPublicKey: _genericPublicKey,
      genericPublicKeyDatetime: _genericPublicKeyDatetime,
      isRequestingOtp: _isRequestingOtp,
      isMakingOtpRequest: _isMakingOtpRequest,
      otpRequestError: _otpRequestError,
      publicKey: _challenge['public_key'],
      scope: _token.scope,
      token: this.getToken(),
      retrievedUser: _retrievedUser,
      currentUserPhoneNumber: this.getOTPNumber(_userDetails.contactDetails),
      userName: this.getUserName(_userDetails),
      isNavigatingToWebAppDuringValidSession:
        _isNavigatingToWebAppDuringValidSession,
    };
  },

  getOTPNumber(data) {
    if (!isPlainObject(data)) {
      return null;
    }

    if (data.phone.mobile) {
      return data.phone.mobile;
    }

    return null;
  },

  getSessionTimeout() {
    return _sessionTimeout;
  },

  getUserName(data) {
    if (!isPlainObject(data)) {
      return null;
    }

    return data.userName;
  },

  isStepUpMode() {
    return _isStepUpMode;
  },

  isStepUpChallengeLoading() {
    return _isStepUpChallengeLoading;
  },

  isStepUpCompleted() {
    return _isStepUpCompleted;
  },

  isTokenLoading() {
    return _isTokenLoading;
  },

  isTokenError() {
    return _isTokenError;
  },

  getPreferredOTPNumber() {
    return _preferredOTPNumber;
  },

  // Changes done for ID&V
  setURLTokenValue(access_token) {
    urlAccessToken = access_token;
  },
  getURLTokenValue() {
    return urlAccessToken;
  },

  getSessionBankID() {
    return sessionBankID;
  },
  getMalwarepacket() {
    return _malwarePacket;
  },
  //Ended changes for ID&V
  getPwReminderFlag() {
    return _showPwResetReminder;
  },
  getIsNavigatingToWebAppDuringValidSession() {
    return _isNavigatingToWebAppDuringValidSession;
  },
  setIsNavigatingToWebAppDuringValidSession(
    isNavigatingToWebAppDuringValidSession
  ) {
    _isNavigatingToWebAppDuringValidSession =
      isNavigatingToWebAppDuringValidSession;
  },
});

SessionStore.dispatchToken = AppDispatcher.register((payload) => {
  const action = payload.action;
  const data = action.data;
  let changeWillOccur;

  switch (action.actionType) {
    case SessionConstants.REQUEST_ACCESS_TOKEN_CREATE:
      _sessionTimeout = false;
      SessionStore.createAuthToken(data.username, data.callback);
      break;
    case SessionConstants.REQUEST_ACCESS_TOKEN_CREATE_SUCCESS:
      SessionStore.saveNewAccessToken(data);

      _isTokenLoading = false;
      _isTokenError = false;

      _isStepUpMode = false;
      _isStepUpCompleted = true;

      _authenticated = true;
      if (action.callback) {
        action.callback(GenericMapperUtils.mapObject(data, {}, camelCase));
      }

      SessionStore.emitChange();
      break;
    case SessionConstants.REQUEST_ACCESS_TOKEN_CREATE_ERROR:
      switch (action.errorType) {
        case SessionConstants.REQUEST_ACCESS_TOKEN_MULTIPLE_ATTEMPTS_ERROR:
          SessionStore.setAllowRetry(false);
          break;
        default:
          _token = {
            ..._token,
            error: true,
          };

          _isTokenLoading = false;
          _isTokenError = true;

          SessionStore.emitChange();
          break;
      }

      break;
    case SessionConstants.REQUEST_ACCESS_TOKEN_MULTIPLE_ATTEMPTS_ERROR:
      SessionStore.setAllowRetry(false);
      break;

    case SessionConstants.REQUEST_ACCESS_TOKEN_RESET:
      InternetBankingActions.navigateToWebTask('WEB-HOLDING-PAGE');
      SessionStore.resetAccessToken(data.token);
      break;
    case SessionConstants.REQUEST_ACCESS_TOKEN_RESET_SUCCESS:
      _token = {};
      _isNavigatingToWebAppDuringValidSession = false;
      SessionStore.emitChange();
      navigateToLoginPage();
      break;

    case SessionConstants.REQUEST_AUTHENTICATION_UPDATE:
      // If we have an index position, we need to update a specific entry and not overwrite the whole array
      if (data.authIndex >= 0 || isString(data.authIndex)) {
        // We can't update a specific entry if the array hasn't yet been defined or if it's not an array
        if (!isArray(_authentication[data.authType])) {
          _authentication[data.authType] = [];
        }

        // Update the specific entry with the authData value
        _authentication[data.authType][data.authIndex] = data.authData;
      } else {
        if (data.authType === CHALLENGE_TYPE.OTP) {
          const sessionId = _challenge['auth_session_id'];
          const publicKey = _challenge['public_key'];

          // TODOxVM - We should have a generic encryption logic
          const encryptedOTPValue = EncryptionUtils.encrypt(
            publicKey,
            `${sessionId}:${data.authData}`
          );

          _authentication[data.authType] = { otp: encryptedOTPValue };
          return;
        }

        // No index specified then set the array to the authData value
        _authentication[data.authType] = data.authData;
      }

      SessionStore.emitChange();
      break;

    case SessionConstants.REQUEST_ACCESS_CHALLENGE_CREATE:
      SessionStore.createAuthChallenge(
        data.userIdentity,
        data.targetScope,
        data.callback
      );
      break;

    case SessionConstants.REQUEST_ACCESS_CHALLENGE_CREATE_SUCCESS:
      _challenge = data;
      _challenge.error = false;

      // Are we trying to automatically log the user in?
      if (_passwordForAutoLogin) {
        SessionStore.completeUserAutoLogin();
      }

      // TODOxVM - strict OTP for now.
      SessionActionCreator.requestPreferredOTPNumber();
      SessionActionCreator.requestOTPAuthentication();

      SessionStore.emitChange();
      break;

    case SessionConstants.REQUEST_ACCESS_CHALLENGE_CREATE_ERROR:
      _challenge.error = true;
      SessionStore.emitChange();
      break;

    case SessionConstants.REQUEST_ACCESS_CHALLENGE_MISSING_DETAILS_ERROR:
      _challengeMissingDetailsError = true;
      SessionStore.emitChange();
      break;

    case SessionConstants.REQUEST_ACCESS_CHALLENGE_RESET:
      changeWillOccur = !isEmpty(_challenge);
      if (changeWillOccur) {
        _challenge = {};
        SessionStore.emitChange();
      }
      break;

    case SessionConstants.REQUEST_OTP_ERROR:
      _otpRequestError = true;
      SessionStore.emitChange();
      break;
    case SessionConstants.REQUEST_OTP_SUCCESS:
      _otpRequestError = false;
      _isRequestingOtp = false;
      _isMakingOtpRequest = false;
      SessionStore.emitChange();
      break;
    case SessionConstants.REQUEST_OTP_AUTHENTICATION_RESET:
      _isRequestingOtp = false;

      // eslint-disable-next-line no-unused-vars
      const { error, ...rest } = _token;
      _token = rest;
      SessionStore.emitChange();
      break;
    case SessionConstants.REQUEST_OTP_AUTHENTICATION:
      if (!_isRequestingOtp) {
        SessionApiUtils.createOTP(
          data.username,
          SessionStore.getAll().accessToken
        );
        _isRequestingOtp = true;
        _isMakingOtpRequest = true;
        SessionStore.emitChange();
      }
      break;

    case SessionConstants.REQUEST_CURRENT_USER_PHONE_NUMBER:
      SessionApiUtils.requestCurrentUserPhoneNumber(
        SessionStore.getAll().accessToken,
        data.callback
      );
      SessionStore.emitChange();
      break;

    case SessionConstants.REQUEST_CURRENT_USER_PHONE_NUMBER_SUCCESS:
      _userDetails = data.userDetails;
      SessionStore.emitChange();
      break;

    case SessionConstants.LOG_USER_IN:
      SessionStore.startUserAutoLogin(data.username, data.password);
      break;

    case SessionConstants.REQUEST_PUBLIC_KEY:
      SessionApiUtils.requestPublicKey(
        SessionStore.getToken()['access_token'],
        data.callback
      );
      break;

    case SessionConstants.REQUEST_PUBLIC_KEY_SUCCESS:
      _genericPublicKey = data.publicKey;
      _genericPublicKeyDatetime = data.datetime;

      setTimeout(() => {
        _genericPublicKey = undefined;
        _genericPublicKeyDatetime = undefined;
        SessionStore.emitChange();
      }, envConfig.publicKeyExpiry);

      data.callback &&
        data.callback(_genericPublicKey, _genericPublicKeyDatetime);

      SessionStore.emitChange();
      break;

    case SessionConstants.REQUEST_BANK_ID:
      SessionApiUtils.requestBankId(data.username, data.callback);
      break;

    case SessionConstants.UPDATE_BANK_ID:
      envConfig.apiBankId = data.bankId;
      SessionStore.emitChange();

      if (data.callback) {
        data.callback();
      }

      break;

    case SessionConstants.GET_CURRENT_USER:
      SessionStore.getCurrentUser(
        _userDetails,
        _retrievedUser,
        _callingGetCurrentUser
      );
      break;

    case SessionConstants.ACCOUNT_LOCKED_ERROR:
      _accountLockedErrorID = action.quoteId;
      break;

    case SessionConstants.SESSION_TIMEOUT:
      _sessionTimeout = true;
      SessionStore.emitChange();
      break;

    case SessionConstants.RESET_SESSION_TIMEOUT:
      _sessionTimeout = false;
      SessionStore.emitChange();
      break;

    // Changes done for ID&V
    case SessionConstants.UPDATE_URL_ACCESS_TOKEN:
      SessionStore.saveNewAccessToken(data);
      _authenticated = true;
      break;

    case SessionConstants.SESSION_BANK_ID_CALL:
      SessionApiUtils.getBankIDData(data);
      break;

    case SessionConstants.SESSION_BANK_ID_SUCCESS:
      sessionBankID = data;
      SessionStore.emitChange();
      break;

    case SessionConstants.SESSION_BANK_ID_ERROR:
      sessionBankIDError = data;
      window.location.href = URLUtils.formatLoginAndRegUrl(
        envConfig.loginAndRegistrationUrl,
        localStorage.getItem('uniqueId'),
        UserManagementStore.isSMEUser()
      );
      break;
    // Ended changes for ID&V

    case SessionConstants.SET_MALWARE_VALUES:
      _malwarePacket = action.data;
      SessionApiUtils.callMalwareApi(_malwarePacket);
      break;

    case SessionConstants.SET_PW_REMINDER_VALUE:
      _showPwResetReminder = action.value;
      SessionStore.emitChange();
      break;

    case SessionConstants.STEP_UP_MODE_INIT: {
      _isStepUpCompleted = false;
      _isStepUpMode = true;
      _isStepUpChallengeLoading = true;

      const { id: userId } = UserManagementStore.getCADetails();
      SessionActionCreator.requestAccessChallengeCreate(userId, 40);

      SessionStore.emitChange();
      break;
    }

    case SessionConstants.REQUEST_PREFERRED_OTP_NUMBER_SUCCESS: {
      _preferredOTPNumber = data;
      _isStepUpChallengeLoading = false;

      SessionStore.emitChange();
      break;
    }

    case SessionConstants.SUBMIT_OTP: {
      const { id: userId } = UserManagementStore.getCADetails();

      SessionActionCreator.requestAuthenticationUpdate({
        authType: 'otp',
        authData: data,
      });

      _isTokenLoading = true;
      _isTokenError = false;

      SessionActionCreator.requestAccessTokenCreate(userId);

      SessionStore.emitChange();
      break;
    }

    case SessionConstants.REQUEST_PREFERRED_OTP_NUMBER: {
      SessionApiUtils.requestPreferredOTPNumberData();
      break;
    }

    case SessionConstants.STEP_UP_CLEAR_SESSION: {
      _isTokenError = false;
      _isStepUpCompleted = false;
      _isStepUpMode = false;

      _preferredOTPNumber = null;

      SessionStore.emitChange();
      break;
    }

    default:
    // Do nothing.
  }
});

if (envConfig.enableWindowStubs) {
  /**
   * For testing only, lets you force in an arbitrary token.
   * e.g. expired tokens.
   *
   * @param  {String} token  Token to set.
   */
  window.injectAccessToken = (token) => {
    _token['access_token'] = token;
    SessionStore.emitChange();
  };
}

export default SessionStore;
